import React, { useState, useRef } from 'react';
import { Button, Card, Upload, notification } from 'antd';
import {
  UploadOutlined,
  LoadingOutlined,
  StopOutlined,
} from '@ant-design/icons';
import Papa from 'papaparse';
import { getFirestore, writeBatch, doc } from 'firebase/firestore';
import Layout from '../../components/layout/Layout';
import { mapData } from '../../hooks/useDataMapping'; // Mapeamento de dados para CSV
import jsonFileHandler from '../../utils/jsonFileHandler'; // Novo utilitário para lidar com JSON

const { Dragger } = Upload;

const CHUNK_SIZE = 1;

const UploadCidTuss = () => {
  const [uploading, setUploading] = useState(false);
  const [logMessages, setLogMessages] = useState([]);
  const [fileQueue, setFileQueue] = useState([]);
  const [lineCount, setLineCount] = useState(0);
  const [totalLines, setTotalLines] = useState(0);
  const [cancel, setCancel] = useState(false);
  const logRef = useRef(null);

  const openNotification = (type, title, description) => {
    notification[type]({
      message: title,
      description: description,
    });
  };

  const handleFileUpload = (fileList) => {
    const newFiles = fileList.map((file) => ({
      file,
      name: file.name,
      size: file.size,
      status: 'Pendente',
      type: file.type,
    }));

    newFiles.sort((a, b) => a.size - b.size);
    setFileQueue([...fileQueue, ...newFiles]);
  };

  const startProcessing = () => {
    if (fileQueue.length === 0 || uploading) return;
    setUploading(true);
    setCancel(false);
    openNotification(
      'info',
      'Processamento Iniciado',
      'O processamento dos arquivos foi iniciado.'
    );
    processNextFile();
  };

  const processNextFile = async () => {
    if (fileQueue.length === 0 || cancel) {
      setUploading(false);
      openNotification(
        'success',
        'Processamento Concluído',
        cancel
          ? 'O processamento foi cancelado.'
          : 'Todos os arquivos foram processados com sucesso.'
      );
      addLogMessage(
        cancel
          ? 'Processamento cancelado.'
          : 'Todos os arquivos foram processados.'
      );
      return;
    }

    const nextFile = fileQueue[0];
    setLineCount(0);
    addLogMessage(`Processando arquivo: ${nextFile.name}`);

    // Verificar se é um JSON ou CSV
    if (nextFile.file.type === 'application/json') {
      processJSON(nextFile);
    } else {
      // Processamento para CSV
      processCSV(nextFile);
    }
  };

  const processCSV = async (file) => {
    Papa.parse(file.file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        setTotalLines(result.data.length);
        addLogMessage(`Total de linhas a processar: ${result.data.length}`);
        processFile(file, result.data);
      },
      error: (error) => {
        addLogMessage(
          `Erro ao processar o arquivo ${file.name}: ${error.message}`
        );
        setFileQueue((queue) => queue.slice(1));
        processNextFile();
      },
    });
  };

  const processJSON = async (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const jsonData = JSON.parse(e.target.result);
      setTotalLines(jsonData.length);
      addLogMessage(`Total de entradas JSON a processar: ${jsonData.length}`);
      processFile(file, jsonData, true); // Passar a flag para JSON
    };
    reader.readAsText(file.file);
  };

  const processFile = async (file, data, isJSON = false) => {
    let batch = [];
    let processedCount = 0;

    for (let i = 0; i < data.length; i++) {
      if (cancel) break;

      const row = data[i];
      const mappedData = isJSON ? jsonFileHandler(row) : mapData(row); // Verifica o tipo de arquivo

      if (mappedData) {
        batch.push(mappedData);
        setLineCount((prev) => prev + 1);
        processedCount++;

        if (batch.length === CHUNK_SIZE || i === data.length - 1) {
          await saveToFirestore(batch);
          batch = [];
        }
      }

      if (i === data.length - 1) {
        addLogMessage(`Arquivo ${file.name} processado com sucesso.`);
        setFileQueue((queue) => queue.slice(1));
        setUploading(false);
        openNotification(
          'success',
          'Processamento Concluído',
          `Upload do arquivo ${file.name} finalizado com sucesso. Total de linhas processadas: ${processedCount}`
        );
        break;
      }
    }
  };

  const saveToFirestore = async (batch) => {
    const db = getFirestore();
    const batchWriter = writeBatch(db);

    try {
      batch.forEach((item) => {
        const docRef = doc(db, item.collection, item.data.id);
        batchWriter.set(docRef, item.data, { merge: true });
        addLogMessage(
          `Salvando: ${item.collection}/${item.data.id} - Dados: ${JSON.stringify(item.data)}`
        );
      });

      await batchWriter.commit();
      addLogMessage(
        `Lote de dados salvo com sucesso. Total de itens: ${batch.length}`
      );
    } catch (error) {
      addLogMessage(`Erro ao salvar lote de dados: ${error.message}`);
    }
  };

  const addLogMessage = (message) => {
    setLogMessages((prevMessages) => [...prevMessages, message]);
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  };

  const cancelProcessing = () => {
    setCancel(true);
    addLogMessage('Cancelamento solicitado...');
    openNotification(
      'warning',
      'Cancelamento Solicitado',
      'O processamento dos arquivos foi cancelado.'
    );
  };

  return (
    <Layout>
      <Card title="Upload CSV/JSON CID/TUSS" style={{ marginTop: 50 }}>
        <Dragger
          beforeUpload={(file, fileList) => {
            handleFileUpload(fileList);
            return false;
          }}
          multiple={true}
          accept=".csv,.json"
          showUploadList={true}
          disabled={uploading || fileQueue.length >= 20}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">
            Selecione ou arraste arquivos CSV ou JSON (Máx. 20)
          </p>
        </Dragger>

        <Button
          type="primary"
          onClick={startProcessing}
          disabled={uploading || fileQueue.length === 0}
          style={{ marginTop: 20 }}
        >
          {uploading ? <LoadingOutlined spin /> : 'Iniciar Processamento'}
        </Button>

        <Button
          type="default"
          onClick={cancelProcessing}
          disabled={!uploading}
          style={{ marginLeft: 10 }}
        >
          <StopOutlined /> Cancelar
        </Button>

        <div style={{ marginTop: 20 }}>
          <p>
            Linhas processadas: {lineCount} de {totalLines}
          </p>
        </div>

        <div
          style={{
            marginTop: 20,
            maxHeight: 200,
            overflowY: 'auto',
            border: '1px solid #ddd',
            padding: 10,
            whiteSpace: 'pre-wrap',
          }}
          ref={logRef}
        >
          {logMessages.map((msg, index) => (
            <p key={index}>
              {typeof msg === 'object' ? JSON.stringify(msg) : msg}
            </p>
          ))}
        </div>
      </Card>
    </Layout>
  );
};

export default UploadCidTuss;
